import { useEffect, useState } from "react";
import { fetchTeamMembers, fetchGalleries } from "../lib/cms";
import { clinicInfo } from "../data";

export default function Staff() {
  const [team, setTeam] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedMember(null);
      }
    };
    if (selectedMember) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedMember]);

  useEffect(() => {
    async function loadData() {
      try {
        const members = await fetchTeamMembers();
        // Sort team members: Johannes, Anders, Katrine, Lise-Lotte, Marianne
        const sortedMembers = [...members].sort((a, b) => {
          const getIndex = (name) => {
            if (name.includes("Johannes")) return 0;
            if (name.includes("Anders")) return 1;
            if (name.includes("Katrine")) return 2;
            if (name.includes("Lise-Lotte")) return 3;
            if (name.includes("Marianne")) return 4;
            return 5;
          };
          return getIndex(a.name) - getIndex(b.name);
        });
        setTeam(sortedMembers);

        const galleries = await fetchGalleries();
        const staffGallery = galleries.find(
          (g) => g.slug?.da === "personalet" || g.slug === "personalet"
        );
        if (staffGallery && staffGallery.images) {
          setGalleryImages(staffGallery.images);
        }
      } catch (error) {
        console.error("Error loading staff data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-24 pb-24">
      {/* Header Banner */}
      <section className="bg-gradient-to-b from-brand-100/50 to-white py-16 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-950">
            Mød vores personale
          </h1>
          <p className="text-lg text-brand-800 max-w-2xl mx-auto">
            Hos Kiropraktisk Klinik Assens møder du altid et erfarent, smilende og professionelt behandlerteam, der glæder sig til at hjælpe dig.
          </p>
        </div>
      </section>

      {/* Staff Members List */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {team.map((member) => {
            const imgUrl = member.image && member.image[0] 
              ? `${member.image[0]}?width=400&quality=85&format=auto` 
              : "https://assets.auxodev.dk/assets/783a3379-7262-480b-b55e-1bf6fb783275/dsc05720.jpg";

            const hasLongDescription = Boolean(member.description);
            const truncatedDescription = member.description && member.description.length > 120
              ? member.description.slice(0, 120).trim() + "..." 
              : (member.description || "Sekretær i klinikken.");

            return (
              <div
                key={member.id || member.name}
                className="flex flex-col rounded-3xl bg-white border border-brand-100 overflow-hidden shadow-xs hover:shadow-md transition-all duration-300"
              >
                {/* Image */}
                <div className="pt-8 flex justify-center">
                  <div 
                    className="w-32 h-32 rounded-full overflow-hidden border-4 border-brand-100 shadow-sm bg-brand-50 cursor-pointer"
                    onClick={() => setSelectedMember(member)}
                  >
                    <img
                      src={imgUrl}
                      alt={member.name}
                      className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col justify-between space-y-4 text-center">
                  <div className="space-y-3 flex-1 flex flex-col justify-between">
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-brand-600 uppercase tracking-wider">
                        {member.title}
                      </p>
                      <h2 
                        className="text-xl font-bold font-display text-brand-950 cursor-pointer hover:text-brand-700 transition-colors"
                        onClick={() => setSelectedMember(member)}
                      >
                        {member.name}
                      </h2>
                      {member.birth_year && (
                        <p className="text-xs text-brand-500 font-medium">
                          Født {member.birth_year}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-1 flex flex-col justify-between pt-2">
                      <p className="text-brand-800 leading-relaxed text-sm whitespace-pre-line">
                        {truncatedDescription}
                      </p>
                      <div className="pt-3">
                        <button
                          type="button"
                          onClick={() => setSelectedMember(member)}
                          className="inline-flex items-center text-xs font-bold text-brand-600 hover:text-brand-700 transition-colors cursor-pointer"
                        >
                          Læs mere <i className="fa-solid fa-chevron-right ml-1 text-[10px]" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Booking CTA Card as the 6th Card for symmetry */}
          <div className="flex flex-col rounded-3xl bg-brand-900 text-white border border-brand-800 overflow-hidden shadow-xs hover:shadow-md transition-all duration-300">
            {/* Icon Area */}
            <div className="pt-8 flex justify-center">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-brand-800 shadow-sm bg-brand-950 flex items-center justify-center">
                <i className="fa-solid fa-calendar-check text-4xl text-brand-300" />
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 flex flex-col justify-between space-y-4 text-center">
              <div className="space-y-3 flex-1 flex flex-col justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-bold text-brand-300 uppercase tracking-wider">
                    Online Booking
                  </p>
                  <h2 className="text-xl font-bold font-display text-white">
                    Har du brug for hjælp?
                  </h2>
                  <p className="text-xs text-brand-300 font-medium">
                    Vi glæder os til at hjælpe dig
                  </p>
                </div>
                
                <div className="flex-1 flex flex-col justify-between pt-2">
                  <p className="text-brand-100 leading-relaxed text-sm">
                    Bestil en tid online eller ring til vores søde sekretærer Marianne og Lotte.
                  </p>
                  
                  <div className="pt-4 space-y-2">
                    <a
                      href={clinicInfo.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center rounded-full bg-white px-4 py-2.5 text-xs font-bold text-brand-900 shadow-sm hover:bg-brand-50 transition-all duration-200 cursor-pointer"
                    >
                      Bestil tid online
                    </a>
                    <a
                      href={`tel:${clinicInfo.phone}`}
                      className="inline-flex w-full items-center justify-center rounded-full bg-brand-800 border border-brand-700 px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-700 transition-colors cursor-pointer"
                    >
                      <i className="fa-solid fa-phone mr-2" />
                      <span>{clinicInfo.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-brand-950/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
          onClick={() => setSelectedMember(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-member-name"
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-8 shadow-2xl border border-brand-100 transition-all scale-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-700 hover:bg-brand-100 hover:text-brand-950 transition-colors cursor-pointer"
              aria-label="Luk dialog"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>

            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pt-2">
              {/* Avatar */}
              <div className="shrink-0">
                <img
                  src={
                    selectedMember.image && selectedMember.image[0]
                      ? `${selectedMember.image[0]}?width=400&quality=85&format=auto`
                      : "https://assets.auxodev.dk/assets/783a3379-7262-480b-b55e-1bf6fb783275/dsc05720.jpg"
                  }
                  alt={selectedMember.name}
                  className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover border-4 border-brand-100 shadow-md"
                />
              </div>

              {/* Info */}
              <div className="flex-1 text-center sm:text-left space-y-4">
                <div>
                  <p className="text-xs font-bold text-brand-600 uppercase tracking-wider">
                    {selectedMember.title}
                  </p>
                  <h2 id="modal-member-name" className="text-2xl sm:text-3xl font-bold font-display text-brand-950">
                    {selectedMember.name}
                  </h2>
                  {selectedMember.birth_year && (
                    <p className="text-sm text-brand-500 font-medium mt-0.5">
                      Født {selectedMember.birth_year}
                    </p>
                  )}
                </div>

                <div className="text-brand-800 leading-relaxed text-sm sm:text-base whitespace-pre-line pt-3 border-t border-brand-100">
                  {selectedMember.description || "Sekretær i klinikken."}
                </div>

                <div className="pt-4 flex flex-wrap gap-3 justify-center sm:justify-start">
                  <a
                    href={clinicInfo.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-full bg-brand-600 px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm hover:bg-brand-700 transition-all cursor-pointer"
                  >
                    Bestil tid hos os
                  </a>
                  <button
                    type="button"
                    onClick={() => setSelectedMember(null)}
                    className="inline-flex items-center justify-center rounded-full bg-brand-50 border border-brand-200 px-5 py-2.5 text-xs sm:text-sm font-bold text-brand-800 hover:bg-brand-100 transition-all cursor-pointer"
                  >
                    Luk
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
